import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Upload,
  Plus,
  Trash2,
  Eye,
  Send,
  CircleDollarSign,
  History,
  ArrowLeftRight,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(value || 0);

// One row's status across the whole lifecycle: parse -> preview -> saved -> executed/fees.
function rowStatusBadge(row) {
  if (row.validation_error) return <Badge className="bg-red-100 text-red-800">{row.validation_error}</Badge>;
  const tr = row.transfer_result;
  const fr = row.fee_result;
  if (tr) {
    const color = tr.status === 'success' ? 'bg-green-100 text-green-800'
      : tr.status === 'partial' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
    return <Badge className={color}>Transfer: {tr.status}</Badge>;
  }
  if (fr) {
    const color = fr.status === 'success' ? 'bg-green-100 text-green-800'
      : fr.status === 'partial' ? 'bg-amber-100 text-amber-800'
      : fr.status.startsWith('skipped') ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-800';
    return <Badge className={color}>Fee: {fr.status}</Badge>;
  }
  return <Badge className="bg-blue-50 text-blue-700">{row.is_ptam ? 'PTAM' : row.brand || 'OK'}</Badge>;
}

function RowsTable({ rows }) {
  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Investor</TableHead>
            <TableHead>Reference</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Leg plan</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} data-testid={`transfer-row-${i}`}>
              <TableCell className="font-mono">{row.code}</TableCell>
              <TableCell>{row.investor_name || '—'}</TableCell>
              <TableCell>{row.reference}</TableCell>
              <TableCell className="text-right">{formatCurrency(row.amount)}</TableCell>
              <TableCell className="text-xs text-slate-500">{(row.leg_plan || []).join(' then ')}</TableCell>
              <TableCell>{rowStatusBadge(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Shared by the Disbursement tab and the Collection & Fees tab — only the
// `type` and whether the Fees action is offered differ.
function TransferBatchForm({ type, allowFees, authHeaders }) {
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' | 'csv'
  const [rows, setRows] = useState([]); // {code, reference, amount}
  const [draft, setDraft] = useState({ code: '', reference: '', amount: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [parsing, setParsing] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [batch, setBatch] = useState(null); // preview or saved/executed batch
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [sendingFees, setSendingFees] = useState(false);

  const addRow = () => {
    if (!draft.code || !draft.reference || !draft.amount) return;
    setRows(prev => [...prev, { code: draft.code.trim(), reference: draft.reference.trim(), amount: parseFloat(draft.amount) }]);
    setDraft({ code: '', reference: '', amount: '' });
    setBatch(null);
  };

  const removeRow = (i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i));
    setBatch(null);
  };

  const parseCsv = async () => {
    if (!csvFile) return;
    setParsing(true);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      const { data } = await axios.post(`${API_URL}/api/admin/internal-transfers/parse-csv`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setRows(data.rows || []);
      setBatch(null);
      if ((data.parse_errors || []).length > 0) {
        toast.warning(`${data.parse_errors.length} row(s) had parse errors and were skipped`);
      }
      toast.success(`Parsed ${data.rows?.length || 0} row(s) from CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setParsing(false);
    }
  };

  const preview = async () => {
    if (rows.length === 0) return;
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/internal-transfers/batches`,
        { type, rows, dry_run: true },
        { headers: authHeaders }
      );
      setBatch(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const confirmAndSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/internal-transfers/batches`,
        { type, rows, dry_run: false },
        { headers: authHeaders }
      );
      setBatch(data);
      toast.success('Batch saved — ready to execute');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const execute = async () => {
    if (!batch?.id) return;
    setExecuting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/internal-transfers/batches/${batch.id}/execute`,
        {},
        { headers: authHeaders }
      );
      setBatch(data);
      toast.success('Batch executed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const sendFees = async () => {
    if (!batch?.id) return;
    setSendingFees(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/internal-transfers/batches/${batch.id}/send-fees`,
        {},
        { headers: authHeaders }
      );
      setBatch(data);
      toast.success('Fees sent to PTAM Client Reserves');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fee transfer failed');
    } finally {
      setSendingFees(false);
    }
  };

  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const isSaved = !!batch?.id;
  const isExecuted = !!batch?.executed_at;
  const ptamRowCount = (batch?.rows || []).filter(r => r.is_ptam && !r.validation_error).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">1. Enter rows</CardTitle>
          <CardDescription>Investor/RP code, reference, and amount per row — same as the "PAYMENT REFERENCES" / "Deal Allocation" sheet columns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={entryMode === 'manual' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEntryMode('manual')}
              data-testid={`${type}-mode-manual`}
            >
              Manual entry
            </Button>
            <Button
              variant={entryMode === 'csv' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEntryMode('csv')}
              data-testid={`${type}-mode-csv`}
            >
              CSV upload
            </Button>
          </div>

          {entryMode === 'csv' ? (
            <div className="flex items-center gap-3">
              <Input
                type="file"
                accept=".csv"
                onChange={e => setCsvFile(e.target.files?.[0] || null)}
                className="max-w-xs"
                data-testid={`${type}-csv-input`}
              />
              <Button onClick={parseCsv} disabled={!csvFile || parsing} variant="outline" data-testid={`${type}-csv-parse-btn`}>
                <Upload className="h-4 w-4 mr-2" />{parsing ? 'Parsing…' : 'Parse CSV'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
              <div>
                <Label className="text-xs">RP / Investor code</Label>
                <Input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} placeholder="e.g. 12-00" data-testid={`${type}-draft-code`} />
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={draft.reference} onChange={e => setDraft({ ...draft, reference: e.target.value })} placeholder="e.g. PTC/12-00:000362" data-testid={`${type}-draft-reference`} />
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" value={draft.amount} onChange={e => setDraft({ ...draft, amount: e.target.value })} placeholder="0.00" data-testid={`${type}-draft-amount`} />
              </div>
              <Button onClick={addRow} data-testid={`${type}-add-row-btn`}>
                <Plus className="h-4 w-4 mr-2" />Add row
              </Button>
            </div>
          )}

          {rows.length > 0 && (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono">{r.code}</TableCell>
                      <TableCell>{r.reference}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.amount)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`${type}-remove-row-${i}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-4 py-2 text-sm text-slate-600 bg-slate-50 border-t">
                {rows.length} row(s) · Total {formatCurrency(total)}
              </div>
            </div>
          )}

          <Button onClick={preview} disabled={rows.length === 0 || previewing} data-testid={`${type}-preview-btn`}>
            <Eye className="h-4 w-4 mr-2" />{previewing ? 'Checking…' : 'Preview'}
          </Button>
        </CardContent>
      </Card>

      {batch && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">2. {isExecuted ? 'Result' : 'Preview'}</CardTitle>
            <CardDescription>
              {isSaved
                ? `Batch ${batch.id} — ${batch.status}${isExecuted ? ` (executed ${new Date(batch.executed_at).toLocaleString()})` : ''}`
                : 'Nothing has been sent or saved yet — this is a dry run.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RowsTable rows={batch.rows} />

            {!isSaved && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Review every row above before confirming — this moves real money once executed and cannot be undone by this screen.</span>
              </div>
            )}

            <div className="flex gap-2">
              {!isSaved && (
                <Button onClick={confirmAndSave} disabled={saving} data-testid={`${type}-confirm-save-btn`}>
                  {saving ? 'Saving…' : 'Confirm & Save Batch'}
                </Button>
              )}
              {isSaved && !isExecuted && (
                <Button onClick={execute} disabled={executing} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${type}-execute-btn`}>
                  <Send className="h-4 w-4 mr-2" />{executing ? 'Sending…' : `Execute ${type === 'disbursement' ? 'Disbursement' : 'Collection'}`}
                </Button>
              )}
              {isSaved && allowFees && (
                <Button
                  onClick={sendFees}
                  disabled={sendingFees || ptamRowCount === 0}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  title={ptamRowCount === 0 ? 'No PTAM rows in this batch' : undefined}
                  data-testid={`${type}-send-fees-btn`}
                >
                  <CircleDollarSign className="h-4 w-4 mr-2" />
                  {sendingFees ? 'Sending…' : `Send Fees to PTAM Reserves (${ptamRowCount})`}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HistoryTab({ authHeaders }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/internal-transfers/batches`, { headers: authHeaders });
      setBatches(data.batches || []);
    } catch (err) {
      toast.error('Failed to load transfer history');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const viewBatch = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/internal-transfers/batches/${id}`, { headers: authHeaders });
      setDetail(data);
    } catch (err) {
      toast.error('Failed to load batch detail');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" />Past batches</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : batches.length === 0 ? (
            <p className="text-sm text-slate-500">No batches yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map(b => (
                  <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                    <TableCell>{new Date(b.created_at).toLocaleString()}</TableCell>
                    <TableCell className="capitalize">{b.type}</TableCell>
                    <TableCell><Badge>{b.status}</Badge></TableCell>
                    <TableCell>{b.created_by_email}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => viewBatch(b.id)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Batch {detail.id}</CardTitle>
            <CardDescription className="capitalize">{detail.type} — {detail.status}</CardDescription>
          </CardHeader>
          <CardContent>
            <RowsTable rows={detail.rows} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const AdminInternalTransfersPage = () => {
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  return (
    <div className="space-y-6" data-testid="admin-internal-transfers-page">
      <div>
        <h1 className="text-2xl font-bold text-[#212529] flex items-center gap-2">
          <ArrowLeftRight className="h-6 w-6" />Transfers
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Portal-native replacement for the internal-transfer Excel macros — Disbursement (main → investor/RP),
          Collection (investor/RP → main), and Fees (PTAM investor/RP → PTAM Client Reserves).
        </p>
      </div>

      <Tabs defaultValue="disbursement">
        <TabsList>
          <TabsTrigger value="disbursement" data-testid="tab-disbursement">Disbursement</TabsTrigger>
          <TabsTrigger value="collection" data-testid="tab-collection">Collection &amp; Fees</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="disbursement" className="mt-6">
          <TransferBatchForm type="disbursement" allowFees={false} authHeaders={authHeaders} />
        </TabsContent>

        <TabsContent value="collection" className="mt-6">
          <TransferBatchForm type="collection" allowFees={true} authHeaders={authHeaders} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <HistoryTab authHeaders={authHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInternalTransfersPage;
